import { IStorage } from "@me8eon/storage";
import { AppendAndGetAsyncStore, AsyncStore, StoreGetAsyncStore } from "./async.storage";
import { Codec, decodeLines, encodeLines, LineToCodec } from "@me8eon/codec";
import { ErrorsOr, flatMapErrorsOr, mapErrorsOr } from "@me8eon/errors";

export function makeAsyncStoreAndGet<Id, Data>(storage: IStorage, codec: Codec<Data>, idToString: (id: Id) => string, valueIfUndefined: (id: Id) => ErrorsOr<Data>): AsyncStore<Id, Data> {
    const store = async (id: Id, data: Data): Promise<ErrorsOr<void>> =>
        mapErrorsOr(codec.encode(data), s => storage.setItem(idToString(id), s));
    const get = async (id: Id, undefinedIfNotIn?: boolean): Promise<ErrorsOr<Data>> => {
        const item = storage.getItem(idToString(id));
        if (item === null) return undefinedIfNotIn ? { value: undefined as any } : valueIfUndefined(id);
        return codec.decode(item);

    };
    const deleteFrom = async (...ids: Id[]): Promise<ErrorsOr<void>> => {
        for (const id of ids) {
            const idString = idToString(id);
            if (storage.getItem(idString) === null) continue; // If it doesn't exist, we don't need to delete it
            storage.removeItem(idToString(id));
        }
        return { value: undefined as any };
    };
    return { store, get, delete: deleteFrom };
}

export function makeAsyncAppendAndGet<Id, Data>(storage: IStorage, codec: LineToCodec<Data>,
                                                idToString: (id: Id) => string,
                                                valueIfUndefined: (id: Id) => ErrorsOr<Data[]>): AppendAndGetAsyncStore<Id, Data> {
    const get = async (id: Id, undefinedIfNotIn?: boolean): Promise<ErrorsOr<Data[]>> => {
        const item = storage.getItem(idToString(id));
        if (item === null) return undefinedIfNotIn ? { value: undefined as any } : valueIfUndefined(id);
        const asArray = decodeLines(codec, item ?? "");
        return asArray;
    };

    const append = async (id: Id, ...data: Data[]): Promise<ErrorsOr<void>> =>
        flatMapErrorsOr(await get(id, true), existing =>
            mapErrorsOr(encodeLines(codec, [...existing || [], ...data]), encoded =>
                storage.setItem(idToString(id), encoded)));

    return { get, append };
}